import { useEffect, useState } from 'react';
import { useKeyboard } from './useKeyboard';
import { tt } from '../i18n';
import { formatMoney, quickTenderOptions } from '@dastarhan/core';

type Method = 'CASH' | 'CARD' | 'KASPI_QR';
export interface PaymentDraft { method: Method; amount: number; tenderedCash?: number; tip?: number }

const METHOD_RU: Record<Method, string> = { CASH: tt('cash'), KASPI_QR: 'Kaspi QR', CARD: tt('card') };

/**
 * Оплата: один способ с «умными купюрами» — или разделение.
 * В разделённом чеке наличная часть вносится точно, сдача не считается:
 * так работает любой сплит на реальных кассах.
 */
export function PayScreen({ items, due, number, discount, order, table, placeName, cashierName, totals, promos, card, onCard, onCardClear, guest, onGuest, onGuestClear, canSplit, onSplit, deposit, onDepositClear, canUnbill, billed, onUnbill, onPromoCode, canDiscount, noDiscountReason, onEditDiscount, onConfirm, onPrint, onBack }: {
  /** состав заказа: кассир видит, за что берёт деньги, а не только сумму */
  items: { itemId: string; name: string; qty: number; unitPrice: number;
    modifiers?: { name: string }[] }[];
  /** шапка чека: тот же вид, что видел официант при закрытии */
  order: { openedAt: string; comment?: string; openedBy?: { name: string } };
  table: { name: string } | null;
  placeName: string;
  cashierName: string;
  totals: { subtotal: number; discount: number; service: number; servicePct: number };
  due: number;
  number: number;
  discount: number;
  /** скидочные строки с происхождением: акция · карта · лояльность */
  promos: { name: string; amount: number; kind?: 'promo' | 'card' | 'loyalty' }[];
  /** приложенная карта гостя; active=false — лояльность дала больше */
  card: { label: string; percent: number; active: boolean } | null;
  onCard: () => void;
  onCardClear: () => void;
  guest: { label: string; percent: number; note?: string | null } | null;
  onGuest: () => void;
  onGuestClear: () => void;
  canSplit: boolean;
  onSplit: () => void;
  deposit: { amount: number; guestName: string } | null;
  /** право вернуть счёт: даётся в офисе, может быть и у официанта */
  canUnbill: boolean;
  /** счёт уже закрыт — только тогда есть что возвращать */
  billed: boolean;
  onUnbill: () => void;
  onPromoCode: () => void;
  onDepositClear: () => void;
  canDiscount: boolean;
  /** почему скидка недоступна — если причина в товарах, а не в правах */
  noDiscountReason?: string | null;
  onEditDiscount: () => void;
  onConfirm: (payments: PaymentDraft[]) => void;
  /** напечатать счёт, не закрывая окно расчёта */
  onPrint: () => void;
  onBack: () => void;
}) {
  const [split, setSplit] = useState(false);

  // Один способ
  const [method, setMethod] = useState<Method>(() => {
    // касса помнит, чем платят чаще всего вчера-сегодня
    const last = localStorage.getItem('dastarhan.lastPayMethod');
    return last === 'CARD' || last === 'KASPI_QR' || last === 'CASH' ? last : 'CASH';
  });
  /**
   * Внесено наличными. Ставим сразу всю сумму: точный расчёт —
   * самый частый случай, а кассир, у которого гость даёт больше,
   * поправит одним касанием.
   *
   * С нуля кнопка «Принять оплату» была погашена, и человек искал,
   * что не так, вместо того чтобы закрыть чек.
   */
  const [tendered, setTendered] = useState(due);
  const [tip, setTip] = useState('');
  // Сумма на весь экран: гость в шумном зале видит, а не переспрашивает
  const [showBig, setShowBig] = useState(false);
  // Двойной тап по «Оплатить» на медленном планшете = два чека
  const [sending, setSending] = useState(false);
  // Esc — отскок к заказу: гость передумал платить, касса не листает меню
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showBig) { setShowBig(false); return; }
      onBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showBig, onBack]);
  // Апсейл-намёк: до круглой тысячи не хватает немного — время десерта
  const roundTo = Math.ceil(due / 1000_00) * 1000_00;
  const gap = roundTo - due;
  const upsell = gap > 0 && gap <= due * 0.2 && due >= 2000_00;
  const [rLang, setRLang] = useState(localStorage.getItem('dastarhan.receiptLang') ?? 'ru');
  const flipLang = () => {
    const next = rLang === 'ru' ? 'kk' : 'ru';
    localStorage.setItem('dastarhan.receiptLang', next);
    setRLang(next);
  };
  const tipVal = Math.round(Number(tip.replace(',', '.')) * 100) || 0;
  const quick = quickTenderOptions(due);
  const change = Math.max(0, tendered - due);
  // Чек мог измениться, пока экран открыт: скидка, гость, разделение
  useEffect(() => { setTendered((t) => (t === 0 || t < due ? due : t)); }, [due]);

  const canSingle = method !== 'CASH' || tendered >= due;

  // Разделение
  const [parts, setParts] = useState<PaymentDraft[]>([]);
  const [draftMethod, setDraftMethod] = useState<Method>('CASH');
  const [draftSum, setDraftSum] = useState('');
  // Цифровая клавиатура для чаевых и суммы: на планшете системная
  // не всплывает, а сумму надо набрать при госте
  const kbTip = useKeyboard(tip, setTip, 'number');
  const kbSum = useKeyboard(draftSum, setDraftSum, 'number');
  const paid = parts.reduce((s, p) => s + p.amount, 0);
  const left = due - paid;
  const draftAmount = Math.round(Number(draftSum.replace(',', '.')) * 100) || 0;

  const addPart = (amount: number) => {
    if (amount <= 0 || amount > left) return;
    setParts((p) => [...p, { method: draftMethod, amount }]);
    setDraftSum('');
  };

  return (
    <div className="pay">
      {/* Верхняя строка: выход и печать. Гость просит счёт ещё раз
          перед тем, как достать деньги — кассир печатает, не выходя
          из расчёта */}
      <div className="pay-top">
        {/* Для закрытого счёта возврат ведёт к столам, а не в набор:
            менять состав уже нельзя, и звать туда кассира незачем.
            Состав он видит здесь же, лентой выше */}
        <button className="btn ghost" onClick={onBack}>
          {billed ? '← К столам' : '← К заказу'}
        </button>
        <button className="btn ghost" onClick={onPrint}>⎙ Печать</button>
      </div>
      <h1>Чек №{number}</h1>

      {/* Тот же гостевой счёт, что видел официант. Кассир сверяет
          бумагу в руке гостя с экраном — вид должен совпадать,
          иначе спорить будут именно здесь, при деньгах */}
      {items.length > 0 && (
        <div className="bill-paper pay-paper">
          <div className="bp-title">ГОСТЕВОЙ СЧЁТ</div>
          <div className="bp-place">{placeName}</div>
          <div className="bp-rule" />
          <div className="bp-row">
            <span>Счёт</span><b>{String(number).padStart(4, '0')}</b>
          </div>
          {table && <div className="bp-row"><span>Стол</span><b>{table.name}</b></div>}
          <div className="bp-row">
            <span>Официант</span><b>{order.openedBy?.name ?? cashierName}</b>
          </div>
          <div className="bp-rule" />
          {items.map((i) => (
            <div key={i.itemId} className="bp-item">
              <div className="bp-name">{i.name}</div>
              {i.modifiers && i.modifiers.length > 0 && (
                <div className="bp-mods">{i.modifiers.map((m) => m.name).join(', ')}</div>
              )}
              <div className="bp-row">
                <span>{i.qty} × {formatMoney(i.unitPrice)}</span>
                <b>{formatMoney(Math.round(i.qty * i.unitPrice))}</b>
              </div>
            </div>
          ))}
          <div className="bp-rule" />
          {totals.service > 0 && (
            <div className="bp-row">
              <span>Обслуживание {totals.servicePct}%</span>
              <b>+{formatMoney(totals.service)}</b>
            </div>
          )}
          <div className="bp-total"><span>Итого</span><b>{formatMoney(due)}</b></div>
        </div>
      )}

      {/* Комментарий официанта: «день рождения», «жалоба по подаче».
          Кассир должен его увидеть до того, как возьмёт деньги */}
      {order.comment && (
        <div className="pay-comment">
          <b>Комментарий к чеку</b>
          <span>{order.comment}</span>
        </div>
      )}

      <div className="due-board">
        <span>{split ? tt('remaining') : tt('due')}</span>
        <span className="money">{formatMoney(split ? left : due)}</span>
      </div>
      {canSplit && (
        <button className="discount-row" onClick={onSplit}>{tt('splitLong')}</button>
      )}
      {/* Возврат счёта в работу: гость посмотрел бумагу и решил взять
          ещё чаю. Стол снова открывается официанту и становится
          зелёным — красный означает «ждём денег», а мы уже не ждём */}
      {canUnbill && billed && (
        <button className="discount-row unbill" onClick={onUnbill}>
          ↩ Вернуть стол официанту
        </button>
      )}

      {/* Промокод остался рядом со скидками: он про цену, а не про стол.
          Кнопка ушла со своего места, но сама возможность нужна —
          гость приходит с кодом от друга */}
      {canDiscount && (
        <button className="discount-row" onClick={onPromoCode}>{tt('promo')}</button>
      )}
      {deposit && (
        <button className="discount-row" onClick={onDepositClear}
          title="Убрать зачёт предоплаты">
          Предоплата по брони ({deposit.guestName}): −{formatMoney(deposit.amount)} · убрать ✕
        </button>
      )}
      {guest?.note && <p className="guest-note" style={{ width: '100%' }}>📝 {guest.note}</p>}
      <button className="discount-row" onClick={guest ? onGuestClear : onGuest}>
        {guest
          ? <>Гость: <b>{guest.label}</b>{guest.percent > 0 && <> · −{guest.percent}%</>} · убрать ✕</>
          : tt('guestByPhone')}
      </button>
      {/* Каждая скидочная строка называет своё происхождение — кассир
          отвечает гостю «откуда минус 450» не гадая. Карта убирается
          крестиком прямо в своей строке */}
      {promos.map((p) => (
        <div key={p.name} className="promo-row">
          <span>
            {p.kind === 'card' ? 'Карта гостя' : p.kind === 'loyalty' ? 'Лояльность гостя' : 'Акция'}: {p.name}
          </span>
          <b className="money">−{formatMoney(p.amount)}</b>
          {p.kind === 'card' && (
            <button className="btn small" onClick={onCardClear}
              title="Убрать карту из чека" aria-label="Убрать карту из чека">✕</button>
          )}
        </div>
      ))}
      {/* Карта приложена, но не сработала — объясняем правило, а не молчим:
          карта и лояльность не складываются, берётся бо́льшая */}
      {card && !card.active && (
        <p className="hint" style={{ margin: 0 }}>
          Карта «{card.label}» −{card.percent}% не применена: лояльность гостя даёт больше,
          а две скидки не складываются — берётся бо́льшая.{' '}
          <button className="btn small" onClick={onCardClear}>Убрать карту</button>
        </p>
      )}
      {!card && (
        <button className="discount-row" onClick={onCard}>
          Скидочная карта или купон — к сканеру
        </button>
      )}
      {canDiscount ? (
        <button className="discount-row" onClick={onEditDiscount}>
          {discount > 0
            ? <>Скидка кассира: <b className="money">−{formatMoney(discount)}</b> · изменить</>
            : 'Дать скидку'}
        </button>
      ) : noDiscountReason ? (
        /* Кнопка не просто исчезает: кассир решит, что сломалось,
           и будет искать её глазами при госте */
        <p className="discount-blocked">{noDiscountReason}</p>
      ) : null}

      {!split && (
        <>
          <div className="methods" role="radiogroup" aria-label="Способ оплаты">
            {(Object.keys(METHOD_RU) as Method[]).map((m) => (
              <button key={m} className={`btn ${method === m ? 'on' : ''}`}
                role="radio" aria-checked={method === m}
                onClick={() => setMethod(m)}>{METHOD_RU[m]}</button>
            ))}
          </div>

          {method === 'CARD' && (
            <label className="cash-input" style={{ marginTop: 8 }}>
              На чай картой, ₸ (сверх чека; необязательно)
              <input {...kbTip.props} inputMode="decimal" className="field" placeholder="0"
                value={tip} onChange={(e) => setTip(e.target.value)} />
              {kbTip.node}
            </label>
          )}
          {method === 'CASH' && (
            <>
              <div className="quick">
                {quick.map((v, i) => (
                  <button key={v} className={`btn ${tendered === v ? 'accent' : ''}`}
                    onClick={() => setTendered(v)}>
                    {i === 0 ? 'Без сдачи' : <span className="money">{formatMoney(v)}</span>}
                  </button>
                ))}
              </div>

              {/* Накопительные кнопки: гость кладёт на стойку 1000, потом
                  500, потом две сотни — кассир повторяет это движением,
                  а не считает сумму в уме. Так работает касса в жизни */}
              <div className="tender-add">
                {[100_00, 500_00, 1000_00, 5000_00].map((step) => (
                  <button key={step} className="btn"
                    onClick={() => setTendered((v) => v + step)}>
                    +{Math.round(step / 100).toLocaleString('ru-RU')}
                  </button>
                ))}
                <button className="btn ghost" onClick={() => setTendered(0)}
                  title="Сбросить внесённое">×</button>
              </div>
              <div className="change-row">
                <span>{tendered >= due ? 'Сдача' : 'Не хватает'}</span>
                <span className="money">{formatMoney(tendered >= due ? change : due - tendered)}</span>
              </div>
            </>
          )}
          {method === 'KASPI_QR' && <p className="hint">Гость сканирует QR на терминале и платит в приложении Kaspi.</p>}
          {method === 'CARD' && <p className="hint">Проведите карту на банковском терминале, затем подтвердите здесь.</p>}

          <button className="btn accent" disabled={!canSingle || sending}
            onClick={() => {
              if (sending) return;
              setSending(true);
              localStorage.setItem('dastarhan.lastPayMethod', method);
              onConfirm([{
              method, amount: due,
              ...(method === 'CASH' ? { tenderedCash: tendered } : {}),
              ...(method === 'CARD' && tipVal > 0 ? { tip: tipVal } : {}),
            }]);
            }}>
            Принять оплату
          </button>
          <button className="btn small" onClick={flipLang}
            title="Язык чека — без похода в настройки">
            Чек: {rLang === 'kk' ? 'ҚАЗ' : 'РУС'}
          </button>
          {upsell && (
            <p className="hint">До {formatMoney(roundTo)} не хватает {formatMoney(gap)} — может, десерт или чай?</p>
          )}
          <button className="btn ghost" onClick={() => setShowBig(true)}>
            👁 Показать сумму гостю
          </button>
          <button className="btn ghost" onClick={() => setSplit(true)}>
            Разделить оплату
          </button>
        </>
      )}

      {split && (
        <>
          {parts.length > 0 && (
            <div className="parts">
              {parts.map((p, i) => (
                <div key={i} className="part-row">
                  <span>{METHOD_RU[p.method]}</span>
                  <span className="money">{formatMoney(p.amount)}</span>
                  <button className="btn small" aria-label="Убрать часть"
                    onClick={() => setParts((x) => x.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          {left > 0 && (
            <>
              <div className="methods" role="radiogroup" aria-label="Способ части">
                {(Object.keys(METHOD_RU) as Method[]).map((m) => (
                  <button key={m} className={`btn ${draftMethod === m ? 'on' : ''}`}
                    role="radio" aria-checked={draftMethod === m}
                    onClick={() => setDraftMethod(m)}>{METHOD_RU[m]}</button>
                ))}
              </div>
              <div className="row" style={{ gap: 6 }}>
                {[2, 3, 4, 5].map((n) => (
                  <button key={n} className="btn small" title={`Поровну на ${n} гостей`}
                    onClick={() => {
                      const share = Math.min(left, Math.ceil(left / n / 100) * 100);
                      setDraftSum(String(share / 100));
                    }}>÷{n}</button>
                ))}
              </div>
              <div className="part-add">
                <input {...kbSum.props} className="field" inputMode="decimal" placeholder="Сумма, ₸"
                  value={draftSum} onChange={(e) => setDraftSum(e.target.value)} />
                {kbSum.node}
                <button className="btn" disabled={draftAmount <= 0 || draftAmount > left}
                  onClick={() => addPart(draftAmount)}>Добавить</button>
                <button className="btn" onClick={() => addPart(left)}>Весь остаток</button>
              </div>
            </>
          )}

          <button className="btn accent" disabled={left !== 0 || parts.length === 0}
            onClick={() => onConfirm(parts)}>
            Принять оплату
          </button>
          <button className="btn ghost"
            onClick={() => { setSplit(false); setParts([]); setDraftSum(''); }}>
            ← Один способ
          </button>
        </>
      )}
      {showBig && (
        <div className="big-due" role="dialog" aria-label="Сумма к оплате"
          onClick={() => setShowBig(false)}>
          <span>{formatMoney(due)}</span>
          <small>Рахмет! · Спасибо!</small>
        </div>
      )}
    </div>
  );
}
